import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Flex, Text, Card, Table, Badge, Button, Box, Tooltip, Dialog,
  TextField, TextArea, Select, IconButton, AlertDialog,
  Popover, CheckboxGroup, Checkbox,
} from "@radix-ui/themes";
import {
  PlusIcon, Pencil1Icon, TrashIcon, MagnifyingGlassIcon,
  ChevronLeftIcon, ChevronRightIcon, ReloadIcon, PersonIcon,
  CheckCircledIcon, CrossCircledIcon, ArrowLeftIcon, MixerHorizontalIcon,
} from "@radix-ui/react-icons";
import {
  useSegments, useCreateSegment, useUpdateSegment, useDeleteSegment,
  useSegmentContacts,
} from "hooks/bMarketing/useSegments";
import { useContactTags } from "hooks/bMarketing/useContacts";
import {
  fieldConfig, operatorsForField, needsValue, emptyRule, RuleRow,
} from "./ContactFilterBuilder";
import { formatInternationalPhoneNumber } from "helpers/phoneFormatting";

// ── Filter field configuration ───────────────────────────────────────────────
// Field config, operators, helpers and the RuleRow component are shared via
// ./ContactFilterBuilder so Segments and Automations stay in sync.
const SOURCE_LABELS = {
  guest_portal: "Guest Portal", sevenrooms: "SevenRooms", opentable: "OpenTable",
  eat_app: "Eat App", mailchimp: "Mailchimp", wix: "Wix", manual: "Manual",
  Opentable: "OpenTable", Mailchimp: "Mailchimp", Wix: "Wix",
};
const SOURCE_COLORS = {
  guest_portal: "blue", sevenrooms: "green", opentable: "orange",
  eat_app: "jade", mailchimp: "yellow", wix: "violet", manual: "purple",
  Opentable: "orange", Mailchimp: "yellow", Wix: "violet",
};
const GENDER_LABELS = {
  male: "Male", female: "Female", other: "Other", unknown: "Unknown", no_response: "No response",
};
const AGE_GROUP_LABELS = {
  under_18: "Under 18", "18_24": "18-24", "25_34": "25-34", "35_44": "35-44",
  "45_54": "45-54", "55_64": "55-64", "65_plus": "65+", no_response: "No response",
};

// Same column configuration as the Contacts tab
const CONTACT_TABLE_COLUMNS = [
  { key: "name", label: "Name", width: "12%" },
  { key: "email", label: "Email", width: "16%" },
  { key: "phone", label: "Phone", width: "10%" },
  { key: "source", label: "Source", width: "8%" },
  { key: "email_marketing", label: "Email Consent", width: "9%" },
  { key: "sms_marketing", label: "SMS Consent", width: "9%" },
  { key: "whatsapp_marketing", label: "WhatsApp Consent", width: "10%" },
  { key: "gender", label: "Gender", width: "7%" },
  { key: "age_group", label: "Age Group", width: "8%" },
  { key: "locations", label: "Locations", width: "11%" },
  { key: "total_visits", label: "Visits", width: "7%" },
  { key: "first_visit", label: "First Visit", width: "10%" },
  { key: "last_visit", label: "Last Visit", width: "10%" },
  { key: "created_at", label: "Created", width: "10%" },
  { key: "updated_at", label: "Updated", width: "10%" },
];

const DEFAULT_VISIBLE_COLUMNS = [
  "name",
  "email",
  "phone",
  "source",
  "email_marketing",
  "sms_marketing",
  "whatsapp_marketing",
  "locations",
];

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ── Truncated text identical to the contacts tab ────────────────────────────

const TruncatedText = ({ children, size, weight, color }) => {
  const text = typeof children === "string" ? children : String(children ?? "");
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const check = () => {
      const el = containerRef.current;
      if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [text]);

  const textEl = (
    <Box as="div" ref={containerRef}
      style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      <Text size={size} weight={weight} color={color}>{text}</Text>
    </Box>
  );
  return isTruncated ? <Tooltip content={text}>{textEl}</Tooltip> : textEl;
};

const ConsentBadge = ({ value }) => (
  <Badge color={value ? "green" : "gray"} variant="soft" size="1">
    {value ? <CheckCircledIcon width="12" height="12" /> : <CrossCircledIcon width="12" height="12" />}
    <Text size="1">{value ? "Opted-in" : "No"}</Text>
  </Badge>
);

// ══════════════════════════════════════════════════════════════════════════════
//  Segment Builder Dialog (create / edit)
// ══════════════════════════════════════════════════════════════════════════════

function SegmentBuilderDialog({ open, onOpenChange, segment, orgId, locations, tags, onSuccess, setToastMessage }) {
  const isEdit = !!segment;
  const createMutation = useCreateSegment();
  const updateMutation = useUpdateSegment();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [matchMode, setMatchMode] = useState("all");
  const [rules, setRules] = useState([emptyRule()]);

  // Populate form when editing
  useEffect(() => {
    if (segment) {
      setName(segment.name || "");
      setDescription(segment.description || "");
      setMatchMode(segment.filters?.match || "all");
      setRules(
        segment.filters?.rules?.length
          ? segment.filters.rules.map((r) => ({ ...r }))
          : [emptyRule()]
      );
    } else {
      setName("");
      setDescription("");
      setMatchMode("all");
      setRules([emptyRule()]);
    }
  }, [segment, open]);

  const handleRuleChange = (idx, updated) => {
    setRules((prev) => prev.map((r, i) => (i === idx ? updated : r)));
  };
  const handleRuleRemove = (idx) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };
  const addRule = () => setRules((prev) => [...prev, emptyRule()]);

  const isValid = useMemo(() => {
    if (!name.trim()) return false;
    if (rules.length === 0) return false;
    return rules.every((r) => {
      if (!r.field || !r.operator) return false;
      if (needsValue(r.operator)) {
        if (Array.isArray(r.value)) return r.value.length > 0;
        return r.value !== "" && r.value !== undefined && r.value !== null;
      }
      return true;
    });
  }, [name, rules]);

  const handleSave = async () => {
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      organisation: orgId,
      filters: { match: matchMode, rules },
    };
    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ segmentId: segment.segment_id, data: payload });
        setToastMessage("Segment updated");
      } else {
        await createMutation.mutateAsync(payload);
        setToastMessage("Segment created");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data;
      const msg = typeof detail === "string" ? detail
        : detail?.filters?.[0] || detail?.name?.[0] || detail?.detail || "Failed to save segment";
      setToastMessage(msg);
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="680px">
        <Dialog.Title>{isEdit ? "Edit segment" : "Create segment"}</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Define filter rules to dynamically group contacts.
        </Dialog.Description>

        <Flex direction="column" gap="4" mt="4">
          {/* Name */}
          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">Name</Text>
            <TextField.Root placeholder="e.g. High-value guests" value={name}
              onChange={(e) => setName(e.target.value)} />
          </Flex>

          {/* Description */}
          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">Description</Text>
            <TextArea placeholder="Optional description…" value={description}
              onChange={(e) => setDescription(e.target.value)} rows={2} />
          </Flex>

          {/* Match mode */}
          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">Match contacts where</Text>
            <Select.Root value={matchMode} onValueChange={setMatchMode}>
              <Select.Trigger style={{ maxWidth: 260 }} />
              <Select.Content>
                <Select.Item value="all">All rules match (AND)</Select.Item>
                <Select.Item value="any">Any rule matches (OR)</Select.Item>
              </Select.Content>
            </Select.Root>
          </Flex>

          {/* Rules */}
          <Flex direction="column" gap="3"
            style={{ background: "var(--gray-a2)", borderRadius: "var(--radius-3)", padding: "var(--space-3)" }}>
            {rules.map((rule, idx) => (
              <RuleRow
                key={idx}
                rule={rule}
                index={idx}
                locations={locations}
                tags={tags}
                onChange={handleRuleChange}
                onRemove={handleRuleRemove}
                matchMode={matchMode}
                totalRules={rules.length}
              />
            ))}
            <Button variant="soft" color="gray" size="1" onClick={addRule} style={{ alignSelf: "flex-start" }}>
              <PlusIcon /> Add rule
            </Button>
          </Flex>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">Cancel</Button>
          </Dialog.Close>
          <Button disabled={!isValid || saving} onClick={handleSave}>
            {saving ? "Saving…" : isEdit ? "Update segment" : "Create segment"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Segment Contacts Viewer (drills into a segment)
// ══════════════════════════════════════════════════════════════════════════════

function SegmentContactsViewer({ segment, onBack, setToastMessage }) {
  const [filters, setFilters] = useState({ search: "", page: 1, page_size: 25 });
  const [searchInput, setSearchInput] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);

  useEffect(() => {
    const t = setTimeout(() => setFilters((p) => ({ ...p, search: searchInput, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { contacts, pagination, isLoading, refetch } = useSegmentContacts(segment?.segment_id, filters);

  const visibleColumnSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);
  const isColumnVisible = (columnKey) => visibleColumnSet.has(columnKey);

  const toggleAllColumns = () => {
    setVisibleColumns((prev) => (
      prev.length === CONTACT_TABLE_COLUMNS.length ? [] : CONTACT_TABLE_COLUMNS.map((column) => column.key)
    ));
  };

  const renderContactCell = (contact, columnKey) => {
    switch (columnKey) {
      case "name":
        return (
          <TruncatedText weight="regular" size="2">
            {contact.full_name || "—"}
          </TruncatedText>
        );
      case "email":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {contact.email_address?.endsWith("@noemailbwifi.com") ? "—" : contact.email_address}
          </TruncatedText>
        );
      case "phone":
        return <TruncatedText weight="regular" size="2" color="gray">{formatInternationalPhoneNumber(contact.phone_number) || "—"}</TruncatedText>;
      case "source":
        return (
          <Badge color={SOURCE_COLORS[contact.source] || "gray"} variant="soft" size="1">
            {SOURCE_LABELS[contact.source] || contact.source || "—"}
          </Badge>
        );
      case "email_marketing":
        return <ConsentBadge value={contact.email_marketing} />;
      case "sms_marketing":
        return <ConsentBadge value={contact.sms_marketing} />;
      case "whatsapp_marketing":
        return <ConsentBadge value={contact.whatsapp_marketing} />;
      case "gender":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {GENDER_LABELS[contact.gender] || contact.gender || "—"}
          </TruncatedText>
        );
      case "age_group":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {AGE_GROUP_LABELS[contact.age_group] || contact.age_group || "—"}
          </TruncatedText>
        );
      case "locations":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {contact.location_names?.length ? contact.location_names.join(", ") : "—"}
          </TruncatedText>
        );
      case "total_visits":
        return <TruncatedText weight="regular" size="2" color="gray">{contact.total_visits ?? 0}</TruncatedText>;
      case "first_visit":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.first_visit)}</TruncatedText>;
      case "last_visit":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.last_visit)}</TruncatedText>;
      case "created_at":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.created_at)}</TruncatedText>;
      case "updated_at":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.updated_at)}</TruncatedText>;
      default:
        return null;
    }
  };

  return (
    <Flex direction="column" gap="4" style={{ alignSelf: "stretch", flex: "1 0 0", width: "100%" }}>
      {/* Back button + header */}
      <Flex align="center" gap="3" style={{ alignSelf: "stretch" }}>
        <IconButton variant="soft" color="gray" onClick={onBack}>
          <ArrowLeftIcon />
        </IconButton>
        <Flex direction="column" gap="0">
          <Text size="3" weight="medium">{segment.name}</Text>
          {segment.description && <Text size="1" color="gray">{segment.description}</Text>}
        </Flex>
        <Badge variant="soft" size="2" color="gray" style={{ marginLeft: "auto" }}>
          {pagination.count} contact{pagination.count !== 1 && "s"}
        </Badge>
      </Flex>

      {/* Search + table controls */}
      <Flex gap="3" align="center" wrap="wrap" justify="between" style={{ alignSelf: "stretch" }}>
        <Box style={{ width: 300 }}>
          <TextField.Root placeholder="Search within segment…" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}>
            <TextField.Slot><MagnifyingGlassIcon height="16" width="16" /></TextField.Slot>
          </TextField.Root>
        </Box>

        <Flex gap="2" align="center">
          <Popover.Root>
            <Popover.Trigger asChild>
              <Button variant="soft" color="gray">
                <MixerHorizontalIcon />
                Columns
              </Button>
            </Popover.Trigger>
            <Popover.Content maxHeight="320px" style={{ width: "220px", padding: 0 }}>
              <Flex direction="column" gap="1" p="2">
                <Flex p="2" align="center" gap="2">
                  <Checkbox
                    size="2"
                    checked={visibleColumns.length === CONTACT_TABLE_COLUMNS.length}
                    onCheckedChange={toggleAllColumns}
                  />
                  <Text size="2">Select all</Text>
                </Flex>
                <CheckboxGroup.Root
                  size="2"
                  value={visibleColumns}
                  onValueChange={setVisibleColumns}
                  style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}
                >
                  {CONTACT_TABLE_COLUMNS.map((column) => (
                    <Flex key={column.key} p="2">
                      <CheckboxGroup.Item value={column.key}>{column.label}</CheckboxGroup.Item>
                    </Flex>
                  ))}
                </CheckboxGroup.Root>
              </Flex>
            </Popover.Content>
          </Popover.Root>
          <Button variant="soft" color="gray" onClick={() => { refetch(); setToastMessage?.("Contacts refreshed"); }}>
            <ReloadIcon />
            Refresh
          </Button>
        </Flex>
      </Flex>

      {/* Table */}
      <Card style={{ display: "flex", alignSelf: "stretch", flexDirection: "column", alignItems: "flex-start",
          flex: "1 0 0", height: "100%", maxWidth: "100%", overflow: "auto" }}>
        {contacts.length === 0 ? (
          <Flex justify="center" align="center" style={{ width: "100%", height: "100%" }}>
            <Text color="gray">{isLoading ? "Loading contacts…" : "No contacts in this segment"}</Text>
          </Flex>
        ) : (
          <Table.Root layout="fixed" style={{ width: "100%", height: "100%" }}>
            <Table.Header style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "var(--color-panel-solid)" }}>
              <Table.Row align="center">
                {CONTACT_TABLE_COLUMNS.filter((column) => isColumnVisible(column.key)).map((column) => (
                  <Table.ColumnHeaderCell key={column.key} style={{ width: column.width }}>
                    <TruncatedText size="2" weight="medium">{column.label}</TruncatedText>
                  </Table.ColumnHeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {contacts.map((c) => (
                <Table.Row key={c.id}>
                  {CONTACT_TABLE_COLUMNS.filter((column) => isColumnVisible(column.key)).map((column) => (
                    <Table.Cell key={column.key}>{renderContactCell(c, column.key)}</Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Card>

      {/* Pagination */}
      {pagination.numPages > 1 && (
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          <Text size="2" color="gray">
            Showing {(pagination.currentPage - 1) * filters.page_size + 1} –{" "}
            {Math.min(pagination.currentPage * filters.page_size, pagination.count)} of{" "}
            {pagination.count} contacts
          </Text>
          <Flex gap="2" align="center">
            <Button variant="soft" disabled={pagination.currentPage <= 1}
              onClick={() => setFilters((p) => ({ ...p, page: p.page - 1 }))}>
              <ChevronLeftIcon /> Previous
            </Button>
            <Text size="2">Page {pagination.currentPage} of {pagination.numPages}</Text>
            <Button variant="soft" disabled={pagination.currentPage >= pagination.numPages}
              onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}>
              Next <ChevronRightIcon />
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Segment rule summary (human-readable badge list)
// ══════════════════════════════════════════════════════════════════════════════

function RuleSummary({ filters, tags }) {
  if (!filters?.rules?.length) return <Text size="1" color="gray">No rules defined</Text>;

  const matchLabel = filters.match === "any" ? "OR" : "AND";

  const tagName = (tagId) => (tags || []).find((t) => t.tag_id === tagId)?.name || tagId;

  return (
    <Flex gap="1" wrap="wrap" align="center">
      {filters.rules.map((rule, idx) => {
        const cfg = fieldConfig(rule.field);
        const label = cfg?.label || rule.field;
        const ops = operatorsForField(rule.field);
        const opLabel = ops.find((o) => o.value === rule.operator)?.label || rule.operator;

        let valueLabel = "";
        if (needsValue(rule.operator)) {
          if (Array.isArray(rule.value)) {
            if (cfg?.type === "select") {
              valueLabel = rule.value
                .map((v) => cfg.options?.find((o) => o.value === v)?.label || v)
                .join(", ");
            } else if (cfg?.type === "tag") {
              valueLabel = rule.value.map(tagName).join(", ");
            } else {
              valueLabel = rule.value.join(" – ");
            }
          } else {
            if (cfg?.type === "select") {
              valueLabel = cfg.options?.find((o) => o.value === rule.value)?.label || rule.value;
            } else if (cfg?.type === "tag") {
              valueLabel = tagName(rule.value);
            } else {
              valueLabel = String(rule.value ?? "");
            }
          }
        }

        return (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <Badge variant="outline" color="gray" size="1">{matchLabel}</Badge>
            )}
            <Badge variant="soft" color="amber" size="1">
              {label} {opLabel} {valueLabel}
            </Badge>
          </React.Fragment>
        );
      })}
    </Flex>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Main Exported Component
// ══════════════════════════════════════════════════════════════════════════════

const SegmentsTab = forwardRef(function SegmentsTab({ orgId, locations, setToastMessage }, ref) {
  const { segments, isLoading, refetch } = useSegments(orgId);
  const { contactTags } = useContactTags(orgId);
  const deleteMutation = useDeleteSegment();

  // Dialog states
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editSegment, setEditSegment] = useState(null);
  const [segmentToDelete, setSegmentToDelete] = useState(null);

  // Drill-in state
  const [viewingSegment, setViewingSegment] = useState(null);

  const handleCreate = () => { setEditSegment(null); setBuilderOpen(true); };
  const handleEdit = (seg) => { setEditSegment(seg); setBuilderOpen(true); };

  // Expose actions to parent via ref
  useImperativeHandle(ref, () => ({
    create: handleCreate,
    refresh: refetch,
  }), [refetch]);
  const handleDelete = async () => {
    if (!segmentToDelete) return;
    try {
      await deleteMutation.mutateAsync(segmentToDelete.segment_id);
      setToastMessage("Segment deleted");
    } catch {
      setToastMessage("Failed to delete segment");
    }
    setSegmentToDelete(null);
  };

  // If viewing a segment's contacts, show that sub-view
  if (viewingSegment) {
    return (
      <SegmentContactsViewer
        segment={viewingSegment}
        onBack={() => setViewingSegment(null)}
        setToastMessage={setToastMessage}
      />
    );
  }

  return (
    <>
      <Flex direction="column" gap="4" style={{ alignSelf: "stretch", flex: "1 0 0", width: "100%" }}>
        {/* Segments list */}
        {isLoading ? (
          <Flex justify="center" align="center" style={{ flex: "1 0 0" }}>
            <Text color="gray">Loading segments…</Text>
          </Flex>
        ) : segments.length === 0 ? (
          <Flex direction="column" gap="2"
            style={{ justifyContent: "center", alignItems: "center", alignSelf: "stretch",
              border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)",
              height: "100%", flex: "1 0 0", padding: "var(--space-6)" }}>
            <Text color="gray" weight="medium">No segments yet</Text>
            <Text size="2" color="gray">Create your first segment to dynamically group contacts.</Text>
          </Flex>
        ) : (
          <Flex direction="column" gap="3" style={{ alignSelf: "stretch" }}>
            {segments.map((seg) => (
              <Card key={seg.segment_id}
                style={{ padding: "var(--space-4)", cursor: "pointer" }}
                onClick={() => setViewingSegment(seg)}>
                <Flex justify="between" align="start" gap="3">
                  <Flex direction="column" gap="2" style={{ flex: "1 0 0" }}>
                    <Flex align="center" gap="2">
                      <Text size="3" weight="medium">{seg.name}</Text>
                      <Badge variant="soft" size="1">
                        <PersonIcon width="12" height="12" />
                        {seg.contact_count?.toLocaleString() ?? 0} contact{seg.contact_count !== 1 && "s"}
                      </Badge>
                      {!seg.is_active && <Badge variant="soft" color="gray" size="1">Inactive</Badge>}
                    </Flex>
                    {seg.description && <Text size="2" color="gray">{seg.description}</Text>}
                    <RuleSummary filters={seg.filters} tags={contactTags} />
                  </Flex>

                  {/* Actions */}
                  <Flex gap="3" onClick={(e) => e.stopPropagation()}>
                    <Tooltip content="Edit segment">
                      <IconButton variant="ghost" size="1" onClick={() => handleEdit(seg)}>
                        <Pencil1Icon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content="Delete segment">
                      <IconButton variant="ghost" color="red" size="1" onClick={() => setSegmentToDelete(seg)}>
                        <TrashIcon />
                      </IconButton>
                    </Tooltip>
                  </Flex>
                </Flex>
              </Card>
            ))}
          </Flex>
        )}
      </Flex>

      {/* Builder dialog */}
      <SegmentBuilderDialog
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        segment={editSegment}
        orgId={orgId}
        locations={locations}
        tags={contactTags}
        onSuccess={refetch}
        setToastMessage={setToastMessage}
      />

      {/* Delete confirmation */}
      <AlertDialog.Root open={!!segmentToDelete} onOpenChange={(open) => !open && setSegmentToDelete(null)}>
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>Delete Segment</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Are you sure you want to delete <Text weight="bold">{segmentToDelete?.name}</Text>?
            This will not delete any contacts, only the segment definition.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" onClick={handleDelete}>Delete</Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </>
  );
});

export default SegmentsTab;
